/**
 * CLI telemetry helpers (decision 016).
 *
 * Three submission sites use this module:
 *  - `handle-error.ts` — auto error events.
 *  - `init` / `plug` / `dev` / `login` — success events on the high-signal verbs.
 *  - `cloudgrid feedback` — explicit user-volunteered feedback.
 *
 * Every site funnels through {@link recordCliEvent}, which:
 *  - Returns immediately if {@link isTelemetryEnabled} is false.
 *  - Returns immediately if no JWT is on disk.
 *  - Constructs a one-shot V2Client and `await`s the 250ms-capped POST.
 *  - Swallows every error path so the caller's lifecycle is unchanged.
 *
 * The module also owns the local credential-scrub regex: the CLI scrubs
 * once before submission, the server scrubs again as defense in depth.
 */

import { readCredentials } from '../credentials.js';
import { loadConfig } from '../config.js';
import { getActiveOrgSlug, getTelemetryConfig } from '../active-org.js';
import { V2Client, type SubmitCliEventInput } from '../api/v2-client.js';

/**
 * Resolve whether telemetry submission should fire. Three inputs in
 * priority order:
 *
 *   1. `CLOUDGRID_TELEMETRY` env — `0` / `false` disables; `1` / `true`
 *      forces on, regardless of config.
 *   2. `~/.cloudgrid/config.yaml` `telemetry` field — `false` disables.
 *   3. Default — enabled.
 *
 * The env var wins over config so an operator can disable telemetry on
 * a single CI run without mutating persistent config.
 */
export function isTelemetryEnabled(): boolean {
  const env = process.env.CLOUDGRID_TELEMETRY;
  if (env !== undefined) {
    const norm = env.trim().toLowerCase();
    if (norm === '0' || norm === 'false' || norm === 'no' || norm === 'off') return false;
    if (norm === '1' || norm === 'true' || norm === 'yes' || norm === 'on') return true;
  }
  const cfg = getTelemetryConfig();
  return cfg !== false;
}

/**
 * Patterns the CLI scrubs from `text` and `log_excerpt` before submission.
 * Mirrors the server's `scrubCredentials` (`telemetry-v2.ts`); the server
 * runs the same pass again as defense in depth. Keep these in sync —
 * if the two drift, scrubbing in one place will mask coverage gaps in
 * the other.
 */
const SCRUB_PATTERNS: ReadonlyArray<RegExp> = [
  /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
  /(password|secret|key|token)\s*[:=]\s*\S+/gi,
  /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]+/g,
  /\bxox[abps]-[A-Za-z0-9-]+/g,
  /Bearer\s+[A-Za-z0-9._\-+/=]+/g,
];

const SCRUB_PLACEHOLDER = '[REDACTED]';

/**
 * Filesystem paths in stack-frame and log lines (decision 016 §"Things
 * this payload deliberately omits" — third-party stack frames can leak
 * project / customer / user names via the filesystem path). Matches a
 * leading slash, 3+ path segments, then a code-file extension. The
 * lookbehind excludes URL-style separators (`://`, `..//path`) so
 * `https://example.com/a/b/c.js` is not scrubbed.
 *
 * Limits, deliberate:
 *  - POSIX-style only. Windows drive paths (`C:\...`) are not scrubbed
 *    in this pass; rare for our pre-release surface and adds complexity.
 *  - 3+ segments minimum. Short paths like `/file.js` are not scrubbed
 *    (too generic to confidently classify as a path).
 *  - Extension allow-list (`js|ts|cjs|mjs|py`) is the set we expect to
 *    see in stack frames. Adding more is a deliberate choice, not a
 *    casual edit.
 */
const PATH_SCRUB_RE = /(?<![\w:/\\])(?:[/\\][\w.-]+){3,}\.(?:js|ts|cjs|mjs|py)/g;
const PATH_SCRUB_PLACEHOLDER = '<scrubbed-path>';

/** Scrub credential-shaped substrings and filesystem paths from CLI input. */
export function scrubLocal(input: string): string {
  let out = input;
  for (const re of SCRUB_PATTERNS) {
    out = out.replace(re, SCRUB_PLACEHOLDER);
  }
  out = out.replace(PATH_SCRUB_RE, PATH_SCRUB_PLACEHOLDER);
  return out;
}

/**
 * Best-effort telemetry submission. Resolves silently on every failure
 * path; the caller never has to wrap this in try/catch.
 *
 * Returns once the request resolves OR the V2Client's 250ms cap fires.
 * Callers in `handle-error.ts` can `await` directly before
 * `process.exit` without holding the process up indefinitely.
 *
 * `opts.force` bypasses the {@link isTelemetryEnabled} check. The only
 * caller that sets it is `cloudgrid feedback`: typing the verb is itself
 * the consent, so the auto-event opt-out doesn't apply (decision 016
 * §"Privacy and anonymization" — the opt-out gates silent paths).
 */
export async function recordCliEvent(
  input: SubmitCliEventInput,
  opts: { force?: boolean } = {},
): Promise<void> {
  if (!opts.force && !isTelemetryEnabled()) return;
  let client: V2Client;
  try {
    const creds = readCredentials();
    if (!creds || !creds.jwt) return;
    const cfg = loadConfig();
    if (!cfg.api_url) return;
    client = new V2Client({
      apiUrl: cfg.api_url,
      jwt: creds.jwt,
      orgSlug: getActiveOrgSlug(),
    });
  } catch {
    // Config/credential read failure → silent. Telemetry is opportunistic.
    return;
  }
  await client.submitCliEvent(input);
}
